import React, { useEffect, useState } from 'react';
import { communityApi as community } from '../services/api';
import { MemberAvatarWithBadge, MemberTypeBadge } from '../components/MemberBadge';
import { Users, MapPin, Store, Ticket, Coins, Award, DollarSign, HeartHandshake, Loader2, Star } from 'lucide-react';

function Stat({ label, value, icon: Icon, testId, tone = 'green' }) {
  const c = {
    green: 'from-green-500/20 to-green-700/10 border-green-500/30 text-green-300',
    blue:  'from-sky-500/20 to-sky-700/10 border-sky-500/30 text-sky-300',
    amber: 'from-amber-500/20 to-amber-700/10 border-amber-500/30 text-amber-300',
    pink:  'from-pink-500/20 to-pink-700/10 border-pink-500/30 text-pink-300',
  }[tone];
  return (
    <div className={`rounded-2xl p-4 border bg-gradient-to-br ${c}`} data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest opacity-80"><Icon size={12}/> {label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-white">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

export default function CommunityPage() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [spotlight, setSpotlight] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([community.stats(), community.members(60), community.spotlight()])
      .then(([s, m, sp]) => { setStats(s); setMembers(m); setSpotlight(sp); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="pbb-card p-10 text-center"><Loader2 className="mx-auto animate-spin text-green-400" size={22}/></div>;
  }

  const usd = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8" data-testid="community-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-black">The Community</h1>
        <p className="text-gray-400 text-sm mt-1">Every registered member — see who's building this movement with you.</p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="community-stats-bar">
        <Stat label="Members" value={stats?.totalMembers || 0} icon={Users} testId="stat-members" />
        <Stat label="Businesses" value={stats?.totalBusinesses || 0} icon={Store} testId="stat-businesses" tone="blue" />
        <Stat label="States" value={stats?.statesRepresented || 0} icon={MapPin} testId="stat-states" tone="amber" />
        <Stat label="Deals" value={stats?.totalDeals || 0} icon={Ticket} testId="stat-deals" tone="pink" />
        <Stat label="Points Earned" value={stats?.totalPointsEarned || 0} icon={Coins} testId="stat-points-earned" />
        <Stat label="Points Redeemed" value={stats?.totalPointsRedeemed || 0} icon={Award} testId="stat-points-redeemed" tone="blue" />
        <Stat label="Cash Paid Out" value={usd(stats?.totalCashPaidOut)} icon={DollarSign} testId="stat-cash-paid" tone="amber" />
        <Stat label="Total Cash Earned by Members" value={usd(stats?.totalCashEarnedByMembers)} icon={HeartHandshake} testId="stat-cash-earned" tone="pink" />
      </div>

      {/* Featured Members Spotlight */}
      {spotlight.length > 0 && (
        <div className="pbb-card p-5" data-testid="community-spotlight">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-yellow-400 font-semibold"><Star size={12}/> Featured Members</div>
          <div className="grid md:grid-cols-3 gap-3 mt-3">
            {spotlight.map(m => (
              <div key={m.id} className="rounded-xl border border-[#1f1f1f] p-3 bg-[#0d1f14]/50" data-testid={`spotlight-${m.id}`}>
                <div className="flex items-start gap-3">
                  <MemberAvatarWithBadge initial={(m.name || '?')[0]} memberType={m.memberType} avatarUrl={m.avatarUrl} size={48}/>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{m.businessName || m.name}</div>
                    <MemberTypeBadge type={m.memberType} className="mt-1" />
                    {m.bio && <p className="text-xs text-gray-400 mt-2 line-clamp-3">{m.bio}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anonymized member wall */}
      <div className="pbb-card p-5" data-testid="community-member-wall">
        <div className="text-xs uppercase tracking-widest text-green-300 font-semibold flex items-center gap-1"><Users size={12}/> Member Wall</div>
        <p className="text-xs text-gray-500 mt-1">Anonymized — showing member types only. Personal info is never exposed.</p>
        <div className="mt-4 grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-3">
          {members.map(m => (
            <MemberAvatarWithBadge key={m.id} initial={m.initial} memberType={m.memberType} size={44} className="mx-auto"/>
          ))}
          {members.length === 0 && <div className="col-span-full text-center text-gray-500 text-sm py-6">No members yet — be the first to register.</div>}
        </div>
      </div>
    </div>
  );
}
